import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Show } from "shared/components";
import { cx } from "shared/utils";
import { TRANSCRIPT_UNAVAILABLE } from "./voicemail";

/**
 * CRM-1777: a voicemail's transcript, collapsed to three lines with a Show more
 * toggle, as the Inbox, the Calls drawer and the Activity log show it.
 * `trailing` sits on the toggle row, e.g. the Inbox's Translate button.
 */
export const VoicemailTranscriptText = ({
  text,
  trailing,
  className
}: {
  text: string;
  trailing?: ReactNode;
  className?: string;
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Measured while clamped: a transcript that fits in three lines gets no
  // toggle. Re-measured when the text changes (a translation swaps it).
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  if (!text)
    return (
      <p className={cx("text-xs font-medium text-content-200", className)}>
        {TRANSCRIPT_UNAVAILABLE}
      </p>
    );

  return (
    <div className={cx("space-y-1", className)}>
      <p
        ref={textRef}
        className={cx(
          "whitespace-pre-line text-sm leading-5 text-content-1000",
          expanded ? "" : "line-clamp-3"
        )}
      >
        {text}
      </p>
      <Show if={overflows || Boolean(trailing)}>
        <div className="flex items-center gap-2">
          <Show if={overflows}>
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              className="text-xs font-medium text-primary-500 transition-colors hover:text-primary-600"
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          </Show>
          {trailing}
        </div>
      </Show>
    </div>
  );
};
